import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class WorkerScheduler {

  private readonly workers: Map<string, Worker> = new Map();

  public registered(
    workerName: string,
  ): boolean {

    return this.workers.has(workerName);
  }

  public register(
    workerName: string,
    worker: Worker,
  ): void {

    if (this.workers.has(workerName)) {
      throw new Error(`Worker ${workerName} already registered!`);
    }

    this.workers.set(workerName, worker);
  }

  public tryRegister(
    workerName: string,
    worker: Worker,
  ): void {

    if (!this.workers.has(workerName)) {
      this.workers.set(workerName, worker);
    }
  }

  public schedule<T, R>(
    workerName: string,
    taskType: string,
    payload: T
  ): Observable<R> {

    if (!this.workers.has(workerName)) {
      throw new Error(`Worker ${workerName} not found`);
    }

    const worker = this.workers.get(workerName)!;

    return new Observable<R>(observer => {

      const messageHandler = (event: MessageEvent) => {

        if (event.data.taskType === taskType) {

          observer.next(event.data.result);
          observer.complete();
        }
      };

      const errorHandler = (error: ErrorEvent) => {

        console.error(`Worker error: ${error.message}`);
        observer.error(error);
      };

      worker.addEventListener('message', messageHandler);
      worker.addEventListener('error', errorHandler);

      worker.postMessage({
        taskType,
        payload
      });

      return () => {

        worker.removeEventListener('message', messageHandler);
        worker.removeEventListener('error', errorHandler);
      };
    });
  }

  public terminateWorker(
    workerName: string
  ): void {

    if (this.workers.has(workerName)) {

      this.workers.get(workerName)!.terminate();

      this.workers.delete(workerName);
    }
  }

  public terminateAllWorkers(): void {

    for (const workerName of this.workers.keys()) {
      this.terminateWorker(workerName);
    }
  }

}
